import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { RolagemDaRota } from '@/components/common/RolagemDaRota'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/utils/clienteDeConsultas'
import { Capacitor } from '@capacitor/core'
import { registerSW } from 'virtual:pwa-register'
import { resolverVersaoNativa } from '@/utils/versao'
import './fontes'
import './index.css'
import 'katex/dist/katex.min.css';
import App from './App'
import { ToastProvider } from './components/common/Toast'
import { PdfJobProvider } from './features/quiz/context/PdfJobContext'

/*
 * A versao NATIVA vem de uma chamada assincrona (`App.getInfo()`) e e lida
 * depois de forma sincrona, pelo limite de erro e pelo relato. Resolver aqui, na
 * subida, e o que faz o cache estar quente quando alguem precisar dela. Falhar
 * nao interrompe nada: sem numero, o relato sai incompleto em vez de nao sair.
 */
void resolverVersaoNativa()

if (!Capacitor.isNativePlatform()) {
  /**
   * A ATUALIZAÇÃO CHEGA NA PRÓXIMA NAVEGAÇÃO, E NÃO POR RECARREGAMENTO.
   *
   * ══════════════════════════════════════════════════════════════════════════
   * O QUE HAVIA
   * ══════════════════════════════════════════════════════════════════════════
   *
   *   onNeedRefresh() { updateSW(true) }   // ativava o SW novo e RECARREGAVA
   *   setInterval(() => registration.update(), 15 * 1000)
   *
   * Somado ao `skipWaiting()` e ao `clients.claim()` do `sw.ts`, o service
   * worker novo assumia o controle ENQUANTO A PÁGINA CARREGAVA, e a página se
   * recarregava sozinha. Reproduzido com perfil persistente: duas navegações
   * para `/`, 1,7 s uma da outra, sem ninguém pedir.
   *
   * Recarregar durante o carregamento inicial é o pior momento possível: o
   * usuário vê tela em branco ou meia-carregada, e conclui que o portal não
   * abre. "Só funciona ao recarregar" é a segunda navegação chegando.
   *
   * ══════════════════════════════════════════════════════════════════════════
   * POR QUE ISTO NÃO CUSTA NADA
   * ══════════════════════════════════════════════════════════════════════════
   *
   * Sem `updateSW(true)`, quem está com a página aberta continua na versão
   * antiga até navegar — e numa sessão de minutos isso é imperceptível. O que
   * se troca é "a versão nova chega em segundos, às vezes quebrando a tela" por
   * "chega na próxima navegação, sempre inteira".
   *
   * O INTERVALO passa de 15 s para 5 min. Cada aba pedia `sw.js` quatro vezes
   * por minuto, indefinidamente, para uma versão que muda algumas vezes por
   * semana — e cada checagem era uma chance de a troca cair junto de um
   * carregamento.
   *
   * Ver achado 94.
   */
  registerSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        setInterval(() => { registration.update() }, 5 * 60 * 1000)
      }
    },
    // `onNeedRefresh` deliberadamente ausente: o SW novo entra em espera, e
    // `skipWaiting`/`claim` do `sw.ts` o ativam na próxima navegação. Nada é
    // forçado sobre uma página em uso.
  })
} else {
  document.documentElement.classList.add('cap-app')
}

// O cliente das consultas mora em `utils/clienteDeConsultas.ts`: quem adianta pedidos antes de a tela montar o usa também.


/*
 * O HTML PRONTO DAS PÁGINAS PÚBLICAS (`scripts/prerender-publicas.mjs`) chega com `#root[data-prerender="<rota>"]`, e
 * fora da rota fotografada a guarda do <head> esconde os filhos do #root enquanto este código carrega. O atributo SAI
 * aqui, antes do primeiro desenho, em QUALQUER rota — com ele, a regra escondia também o que o React desenha (a tela
 * branca de 25/10/2026 em toda rota logada aberta direto). Fora da rota fotografada, o conteúdo pronto sai junto: a
 * página inicial não aparece por um instante em /teacher.
 */
function soltarHtmlPronto(raiz: HTMLElement) {
  const rotaPronta = raiz.getAttribute('data-prerender')
  if (rotaPronta === null) return
  const rota = location.pathname.replace(/\/+$/, '') || '/'
  if (rotaPronta !== rota) raiz.replaceChildren()
  raiz.removeAttribute('data-prerender')
}

function desenhar() {
  const raiz = document.getElementById('root')!
  soltarHtmlPronto(raiz)
  ReactDOM.createRoot(raiz).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {/* A página nova abre no topo; a volta do navegador, onde estava. AQUI, e não no `App`: o limite de erro remonta
              tudo o que está dentro dele a cada caminho (`key={pathname}`), e a lembrança da rota anterior se perderia. */}
          <RolagemDaRota />
          <ToastProvider>
            <PdfJobProvider>
              <App />
            </PdfJobProvider>
          </ToastProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  )
}

/*
 * A TROCA DAS TELAS NA ABERTURA (1.4; `features/aplicativo/trocaNaAbertura.ts`): no aplicativo, ANTES de desenhar login ou
 * lista, aplica o pacote pendente de uma abertura anterior — com "Atualizando…" na tela e nunca com prova guardada no
 * aparelho. Falha ou passa de 5 s: segue com as telas atuais. No navegador, desenha direto.
 */
if (Capacitor.isNativePlatform()) {
  const mostrarAtualizando = () => {
    const raiz = document.getElementById('root')
    if (raiz) raiz.innerHTML = '<div style="display:flex;min-height:100vh;align-items:center;justify-content:center;font:16px system-ui,sans-serif;color:#1f2937;background:#fff" role="status" aria-live="polite">Atualizando…</div>'
  }
  void (async () => {
    try {
      const [{ trocarNaAbertura }, { ABERTURA_ATUAL }] = await Promise.all([
        import('./features/aplicativo/trocaNaAbertura'),
        import('./features/aplicativo/trocaDasTelas'),
      ])
      const resultado = await trocarNaAbertura(mostrarAtualizando, ABERTURA_ATUAL)
      // 'aplicando': o `set` já pediu o recarregamento nas telas novas. Se ele não vier em 3 s, desenha as atuais.
      if (resultado === 'aplicando') await new Promise((r) => setTimeout(r, 3000))
    } catch { /* até o módulo da troca falhar ao carregar: a abertura segue */ }
    desenhar()
  })()
} else {
  desenhar()
}
